import { Service } from "../../../glue/Kernel.js";
import { WEAPONS, DEFAULT_LOADOUT } from "../../../content/index.js";
import { coolingDown } from "./select.js";
import { ownedBlocks, hoverIndex } from "../render/wheel.js";
import { getSpriteVM } from "../render/sprites.js";
import { loadOverrides, saveOverrides } from "./keybinds.js";
import CONTROLS from "../../../../data/controls.json" with { type: "json" };

// Keybindings, from data/controls.json: `move` are the held run/jump keys, `actions` the
// discrete ones (open console, chat, scores).
const KEYS = CONTROLS.move;
const ACT = CONTROLS.actions;

// The discrete actions the keybind menu (Set 12.2) can rebind. Movement stays WASD/arrows, and
// console + settings are fixed escape hatches so you can never lock yourself out of the menus. Plugin
// controls and menu-open keys are added at runtime from `welcome` (see bindables()).
const REBINDABLE = [
    { id: "interact", label: "Interact / use", desc: "Pick up or use what you're near", def: ACT.interact },
    { id: "jet", label: "Jetpack", desc: "Toggle the jetpack; hold to jettison it", def: ACT.jet },
    { id: "inventory", label: "Block inventory", desc: "Open your block stash", def: ACT.inventory },
    { id: "blockWheel", label: "Block wheel", desc: "Quick-pick a block to build with", def: ACT.blockWheel },
    { id: "scores", label: "Scoreboard", desc: "Show the scores while held", def: ACT.scores },
    { id: "sayAll", label: "Chat — all", desc: "Message everyone", def: ACT.sayAll },
    { id: "sayTeam", label: "Chat — team", desc: "Message your team only", def: ACT.sayTeam },
];

// Keyboard, mouse, wheel, and touch — the player's half of the loop. It writes the
// Client's input struct (net), asks `ui` who owns the keyboard (console / say bar / join
// panel) and whether we're in a game, and reaches `render` for the world-space aim
// (its `view`) and to flash the deny ring. It owns the live mouse position and the
// scoreboard toggle, which RenderService reads for the crosshair and HUD.
export class InputService extends Service {
    on_phase_init() {
        this.mouse = { x: innerWidth / 2, y: innerHeight / 2 };
        this.showScores = false; // Tab
    }

    on_phase_consolidate() {
        this.net = this.serviceAPI.require("net");
        this.ui = this.serviceAPI.require("ui");
        this.render = this.serviceAPI.require("render");
        this.client = this.net.client;
        this.overrides = loadOverrides(); // saved keybind overrides (localStorage)
        this._capturing = null;           // action id we're capturing a new key for (rebind), or null
        this._wire();
    }

    // --- keybinds (Set 12.2): the current key for an action, rebinding, and the menu's data ----------
    // The live key for a rebindable action: the player's override, else its declared default.
    key(id, def) { return this.overrides[id] || def; }

    // Fire a plugin control / client on_input handler bound to `code` — a keycode OR a "WheelUp"/"WheelDown"
    // pseudo-code (Set 12.9: the scroll wheel is a first-class bindable control, so the shooter-hotbar plugin
    // can own it). Mirrors the keydown dispatch: a client-side on_input wins, else a server-side control.
    // Returns true if something consumed it.
    _dispatchControl(code, ev = {}) {
        const world = this.render.worldAt(this.mouse.x, this.mouse.y);
        const vm = getSpriteVM();
        if (vm) {
            // MODIFIED binds win over plain ones on the same key (Set 16), so shift+scroll can mean
            // something different from scroll — and a plain bind still works when no modifier is down,
            // which is what keeps the wheel switching weapons. A shift-bind with nothing declaring it
            // simply falls through to the plain one rather than swallowing the input.
            const shift = !!ev.shift;
            const hit = this.bindables()
                .filter((b) => b.key === code && vm.hasInput(b.id) && (b.mods === "shift" ? shift : true))
                .sort((a, b) => (b.mods === "shift" ? 1 : 0) - (a.mods === "shift" ? 1 : 0))[0];
            if (hit) {
                // A `hold` bind hears the release too, as { down = 0 }; everything else is a press.
                if (!hit.hold && ev.down === 0) return true;
                vm.fireInput(hit.id, { ...world, down: ev.down === 0 ? 0 : 1 });
                return true;
            }
        }
        if (ev.down === 0) return false;   // server controls are edge presses only
        const ctl = this.client.controls.find((c) => c.spec && this.key(c.id, c.spec.default) === code);
        if (ctl) { this.client.pressControl(ctl.id, world); return true; }
        return false;
    }

    // Does any loaded client plugin want this key on RELEASE (a `hold` bind)? Cheap enough to ask per
    // keyup, and it keeps the keyup handler from having to know what a bindable is.
    _holdBound(code) {
        const vm = getSpriteVM();
        return !!(vm && vm.binds && this.bindables().some((b) => b.hold && b.key === code && vm.hasInput(b.id)));
    }
    rebind(id, key) { if (key) { this.overrides[id] = key; saveOverrides(this.overrides); } }
    resetBinds() { this.overrides = {}; saveOverrides(this.overrides); }
    beginRebind(id, cb) { this._capturing = id; this._captureCb = cb; } // next keypress rebinds `id`

    // Everything the keybind menu lists: the core rebindables + this room's plugin controls + menus.
    bindables() {
        const out = REBINDABLE.map((a) => ({ id: a.id, label: a.label, desc: a.desc || "", key: this.key(a.id, a.def) }));
        // Plugin controls + menu-open keys carry their own human label/description; the "from a plugin"
        // fact moves into the description so the label itself stays clean (Set 12.6).
        for (const c of this.client.controls || []) out.push({ id: c.id, label: c.spec.label || c.name, desc: c.spec.desc || "Plugin control", key: this.key(c.id, c.spec.default) });
        for (const m of this.client.menus || []) out.push({ id: m.id, label: m.spec.title || m.name, desc: "Opens a menu", key: this.key(m.id, m.spec.open) });
        // Bindables a CLIENT-ONLY plugin declared for itself (Set 16). A server plugin's controls ride
        // `welcome`; a `.client.lua` has no server half to declare anything, so it says so in the VM.
        const vm = getSpriteVM();
        if (vm && vm.binds) for (const b of vm.binds()) {
            out.push({ id: b.id, label: b.spec.label || b.id, desc: b.spec.desc || "Plugin control", key: this.key(b.id, b.spec.default), mods: b.spec.mods || "", hold: !!b.spec.hold });
        }
        return out;
    }

    // key -> [action labels] for any key on two+ actions — the menu paints these rows red.
    bindConflicts() {
        const byKey = {}, out = {};
        for (const b of this.bindables()) if (b.key) (byKey[b.key] || (byKey[b.key] = [])).push(b.label);
        for (const k in byKey) if (byKey[k].length > 1) out[k] = byKey[k];
        return out;
    }

    on_phase_ready() {
        // Push the current input every 33ms (the render loop is separate, at rAF rate).
        this._tick = setInterval(() => this.sendInput(), 33);
    }

    // Every held key is released the moment a text field takes focus. Without this,
    // opening the console mid-run leaves `right` stuck down and you jog off into the void
    // while typing — the keyup lands on the input, not on us.
    releaseAll() {
        const input = this.client.input;
        for (const k of Object.values(KEYS)) input[k] = 0;
        input.fire = 0;
        input.use = 0;
        input.jet = 0;
        input.shift = 0;
    }

    // Is the keyboard somebody else's right now? The console and say bar know about
    // themselves, but the join panel is just DOM — so ask the document what has focus
    // rather than keeping a list of fields to remember to update. Miss one and typing
    // "test" into a form pops the chat bar, because T is a keybind.
    typingInPage() {
        if (this.ui.con.isTyping()) return true;
        const el = document.activeElement;
        if (!el || el === document.body) return false;
        return el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);
    }

    // Are we actually in a game? Before that there's nothing to move, nobody to chat to
    // and no scoreboard to show — so none of those keys should do anything, let alone
    // while you're filling in the join form.
    inGame() { return this.client.selfCid != null && this.ui.overlayHidden(); }

    // Is the block-placing trowel the weapon in hand? (Only then does the block wheel open.)
    trowelSelected() {
        const name = DEFAULT_LOADOUT[this.client.input.weapon | 0];
        return !!(name && WEAPONS[name] && WEAPONS[name].behavior === "build");
    }

    // Which block the wheel's pointer is over right now — the wheel is centred on screen,
    // its wedges are the materials you're carrying. Null if the pointer's in the dead zone.
    wheelHover() {
        const owned = ownedBlocks(this.client.selfScore()?.inv);
        const idx = hoverIndex(this.mouse.x - innerWidth / 2, this.mouse.y - innerHeight / 2, owned.length);
        return idx >= 0 ? owned[idx].name : null;
    }

    // Which option INDEX of the open radial picker the pointer is over, or -1 (Set 21.5). Same centred radial.
    pickerHover() {
        const p = this.ui.picker; if (!p || !p.options.length) return -1;
        return hoverIndex(this.mouse.x - innerWidth / 2, this.mouse.y - innerHeight / 2, p.options.length);
    }

    // Grid mode = the grid-inventory plugin suppressed the default hotbar (welcome sent hotbar:false); in
    // grid mode the inventory KEY is left to the plugin's client.lua (it opens the macro-widget modal),
    // so the engine's block-stash panel steps aside.
    gridMode() { return this.client.hotbar === false; }
    // The plugin's open macro-widget modal (Set 12.5), or null. The modal's MOUSE (hover/click/drag) is
    // handled natively by the DOM overlay (widget-dom.js); InputService only owns its KEYBOARD (Esc /
    // the open key close it) + keeping the game keys from firing behind it.
    widgetModal() { const vm = getSpriteVM(); return vm && vm.openModal ? vm.openModal() : null; }

    // Which option of the open plugin menu the pointer is over (index), or -1 — the same radial
    // hit-test the block wheel uses, so the highlight lands where the click will commit.
    menuHover() {
        const menu = this.ui.menuOpen;
        if (!menu) return -1;
        return hoverIndex(this.mouse.x - innerWidth / 2, this.mouse.y - innerHeight / 2, (menu.spec.options || []).length);
    }

    touchAim(e) {
        const t = e.touches[0];
        if (t) { this.mouse.x = t.clientX; this.mouse.y = t.clientY; }
    }

    sendInput() {
        this.client.sendInput(this.render.worldAt(this.mouse.x, this.mouse.y));
    }

    _wire() {
        const con = this.ui.con;
        const canvas = this.render.canvas;
        const client = this.client;

        addEventListener("keydown", (e) => {
            // Rebind capture (keybind menu): the next keypress becomes the new binding for the action
            // being rebound; Esc cancels. Swallowed so it doesn't also trigger its old action.
            if (this._capturing) {
                if (e.code !== "Escape") this.rebind(this._capturing, e.code);
                this._capturing = null;
                if (this._captureCb) this._captureCb();
                e.preventDefault(); return;
            }
            // While the console or say bar is up, the keyboard belongs to them — except
            // for the key that closes the console, which is how you get back out.
            if (con.isTyping()) {
                if (e.code === ACT.console) { con.close(); e.preventDefault(); }
                return;
            }
            if (this.typingInPage()) return;
            // The settings overlay (Esc) swallows the game keys while it's up; Esc closes it.
            if (this.ui.settingsOpen()) { if (e.code === ACT.settings) { this.ui.toggleSettings(); e.preventDefault(); } return; }
            // The block inventory (I) and the trowel's block wheel (C) grab the keyboard the
            // same way — their own key, or Esc, backs out; everything else is swallowed.
            if (this.ui.inventoryOpen()) { if (e.code === this.key("inventory", ACT.inventory) || e.code === ACT.settings) { this.ui.toggleInventory(); e.preventDefault(); } return; }
            if (this.ui.wheelIsOpen()) { if (e.code === this.key("blockWheel", ACT.blockWheel) || e.code === ACT.settings) { this.ui.closeWheel(); e.preventDefault(); } return; }
            // A plugin template menu (UI framework D) grabs the keyboard while open — its own key or Esc backs out.
            if (this.ui.menuIsOpen()) { if (e.code === ACT.settings || e.code === this.key(this.ui.menuOpen.id, this.ui.menuOpen.spec.open)) { this.ui.closeMenu(); e.preventDefault(); } return; }
            // A plugin's item wheel (Set 12.5) grabs the keyboard while open — Esc cancels the pick.
            if (this.ui.pickerIsOpen()) { if (e.code === ACT.settings) { this.ui.closePicker(null); e.preventDefault(); } return; }
            // A plugin's open macro-widget modal (Set 12.5) grabs the keyboard: Esc closes it (the engine
            // clears its open flag); its own open key still routes to the plugin's client.lua (which
            // toggles it shut); everything else is swallowed so the game keys don't fire behind it.
            {
                const wm = this.widgetModal();
                if (wm) {
                    if (e.code === ACT.settings) { wm.open = false; e.preventDefault(); return; }
                    const vm = getSpriteVM();
                    for (const b of this.bindables()) if (b.key === e.code && vm && vm.hasInput(b.id)) { vm.fireInput(b.id, this.render.worldAt(this.mouse.x, this.mouse.y)); e.preventDefault(); return; }
                    e.preventDefault(); return;
                }
            }
            // The console works before you've joined — that's where `connect` lives.
            if (e.code === ACT.console) { this.releaseAll(); con.open(); e.preventDefault(); return; }
            if (!this.inGame()) return;
            // Held SHIFT = swim toward the cursor in water (Set 19.1). Track it as a synced flag but don't
            // consume the event — Shift is still a modifier for other binds (shift+scroll zoom).
            if (e.code === "ShiftLeft" || e.code === "ShiftRight") client.input.shift = 1;
            if (e.code === ACT.settings) { this.releaseAll(); this.ui.toggleSettings(); e.preventDefault(); return; }
            // The inventory key opens the block stash panel — EXCEPT in grid mode, where the grid-inventory
            // plugin's client.lua owns that key (it opens the macro-widget modal via the runtime dispatch
            // below), so the engine panel steps aside.
            if (e.code === this.key("inventory", ACT.inventory) && !this.gridMode()) {
                this.releaseAll(); this.ui.toggleInventory(); e.preventDefault(); return;
            }
            // The block wheel only makes sense with the trowel in hand (it configures it).
            if (e.code === this.key("blockWheel", ACT.blockWheel)) {
                if (this.trowelSelected()) { this.releaseAll(); this.ui.openWheel(); this.ui.wheelSel = this.wheelHover(); }
                e.preventDefault(); return;
            }

            if (e.code === this.key("sayAll", ACT.sayAll)) { this.releaseAll(); con.openSay(false); e.preventDefault(); return; }
            if (e.code === this.key("sayTeam", ACT.sayTeam)) { this.releaseAll(); con.openSay(true); e.preventDefault(); return; }
            if (e.code === this.key("scores", ACT.scores)) { this.showScores = true; e.preventDefault(); return; }
            if (e.code === this.key("interact", ACT.interact)) { client.input.use = 1; e.preventDefault(); return; }
            if (e.code === this.key("jet", ACT.jet)) { client.input.jet = 1; e.preventDefault(); return; }

            // A plugin template menu's open key opens the radial (UI framework D).
            const menu = this.client.menus.find((m) => m.spec && this.key(m.id, m.spec.open) === e.code);
            if (menu) { this.releaseAll(); this.ui.openMenu(menu); this.ui.menuSel = this.menuHover(); e.preventDefault(); return; }

            // Client-plugin runtime (Set 12.5): if a plugin's client.lua registered on_input for a bound
            // action, that key runs on the CLIENT — the handler can open a wheel, hold state, and send()
            // to its own server half. Takes precedence over a same-keyed server control.
            if (this._dispatchControl(e.code, { shift: e.shiftKey, down: 1 })) { e.preventDefault(); return; }

            // Plugin controls (Set 12.2): a declared control's key routes an edge press to the server,
            // which fires the plugin's handler on this player. (Default key for now; the keybind menu
            // will make these rebindable through the store in src/client/services/input/keybinds.js.)
            const ctl = this.client.controls.find((c) => c.spec && this.key(c.id, c.spec.default) === e.code);
            if (ctl) { client.pressControl(ctl.id, this.render.worldAt(this.mouse.x, this.mouse.y)); e.preventDefault(); return; }

            if (KEYS[e.code]) { client.input[KEYS[e.code]] = 1; if (e.code === "Space") e.preventDefault(); }
            // (Number-key weapon select is a hotbar PLUGIN control now — Set 12.10 — reached via the plugin-
            // control dispatch above, not a built-in Digit handler.)
        });
        addEventListener("keyup", (e) => {
            if (e.code === "ShiftLeft" || e.code === "ShiftRight") this.client.input.shift = 0;
            if (con.isTyping() || this.typingInPage() || !this.inGame()) return;
            if (e.code === this.key("scores", ACT.scores)) { this.showScores = false; e.preventDefault(); return; }
            if (this._holdBound(e.code) && this._dispatchControl(e.code, { shift: e.shiftKey, down: 0 })) { e.preventDefault(); return; }
            if (e.code === this.key("interact", ACT.interact)) { client.input.use = 0; e.preventDefault(); return; }
            if (e.code === this.key("jet", ACT.jet)) { client.input.jet = 0; e.preventDefault(); return; }
            if (KEYS[e.code]) client.input[KEYS[e.code]] = 0;
        });
        // Losing the window with a key held would otherwise leave it held forever.
        addEventListener("blur", () => this.releaseAll());
        canvas.addEventListener("mousemove", (e) => {
            this.mouse.x = e.clientX;
            this.mouse.y = e.clientY;
            if (this.ui.wheelIsOpen()) this.ui.wheelSel = this.wheelHover(); // track the hovered wedge
            if (this.ui.menuIsOpen()) this.ui.menuSel = this.menuHover();    // track the hovered menu option
            if (this.ui.pickerIsOpen()) this.ui.picker.sel = this.pickerHover(); // radial picker hover
            // (Macro-widget hover is native CSS on the DOM overlay — nothing to track here.)
        });
        canvas.addEventListener("contextmenu", (e) => e.preventDefault()); // right-click is the USE action, not a menu
        canvas.addEventListener("mousedown", (e) => {
            // With a plugin menu up, a click commits the option under the pointer (never fires the
            // weapon underneath). A click in the dead centre just closes it.
            if (this.ui.menuIsOpen()) {
                const i = this.menuHover();
                const opt = i >= 0 ? (this.ui.menuOpen.spec.options || [])[i] : null;
                if (opt) client.selectMenu(this.ui.menuOpen.id, opt.id, this.render.worldAt(this.mouse.x, this.mouse.y));
                this.ui.closeMenu();
                return;
            }
            // With the block wheel up, a click commits the wedge under the pointer (and
            // never fires the weapon underneath it).
            if (this.ui.wheelIsOpen()) {
                const sel = this.wheelHover();
                if (sel) this.net.client.selectBlock(sel);
                this.ui.closeWheel();
                return;
            }
            // The radial picker (Set 21.5): a click commits the option under the pointer (or cancels if in
            // the dead centre) — the pick resolves the plugin's ui.wheel on_pick callback on the client.
            if (this.ui.pickerIsOpen()) {
                const idx = this.pickerHover(), opts = this.ui.picker.options;
                this.ui.closePicker(idx >= 0 && opts[idx] ? opts[idx].value : null);
                return;
            }
            // A macro-widget modal (Set 12.5) is a DOM overlay: its cells handle their own clicks (and the
            // dim swallows the rest), so a press while it's open must not also fire the weapon behind it.
            if (this.widgetModal()) return;
            if (con.isTyping() || this.ui.settingsOpen() || this.ui.inventoryOpen() || !this.inGame()) return; // don't act through an overlay or the join panel
            // Right-click is the USE action (Set 20.3) — the secondary "interact / use what's in hand" verb
            // (place a campfire, eat, open a machine). Left-click stays FIRE (shoot / build / mine).
            if (e && e.button === 2) { client.input.use = 1; this.sendInput(); return; }
            client.input.fire = 1;
            // Deliver the press immediately instead of waiting for the next 33ms sample —
            // otherwise a fast tap (down+up inside one frame) is dropped and feels dead.
            this.sendInput();
            // If the selected weapon is still cooling down, flash a "denied" ring so the
            // click reads as "not ready yet" rather than an unresponsive control.
            if (coolingDown(client.selfScore())) this.render.pushDeny(this.mouse.x, this.mouse.y);
        });
        addEventListener("mouseup", (e) => { if (e && e.button === 2) client.input.use = 0; else client.input.fire = 0; });
        canvas.addEventListener("wheel", (e) => {
            const code = e.deltaY > 0 ? "WheelDown" : "WheelUp"; // scroll as a bindable pseudo-code (Set 12.9)
            // Rebind capture: binding an action TO the wheel in the keybind menu (Esc, a keydown, cancels).
            if (this._capturing) {
                this.rebind(this._capturing, code); this._capturing = null;
                if (this._captureCb) this._captureCb();
                e.preventDefault(); return;
            }
            if (this.ui.wheelIsOpen() || this.ui.inventoryOpen() || this.widgetModal() || this.ui.settingsOpen() || this.ui.menuIsOpen()) return; // an overlay owns the input
            // The scroll wheel is a hotbar-PLUGIN control now (Set 12.10): a plugin bound to this direction
            // owns it. The engine no longer has a built-in weapon cycle — a game with no hotbar plugin just
            // doesn't scroll-switch (the shooter hotbar is the meme-wars costume, not the bare engine).
            if (this._dispatchControl(code, { shift: e.shiftKey, down: 1 })) e.preventDefault();
        }, { passive: false });
        // touch: drag to move+aim is complex; v1 keeps mouse/keyboard, tap fires.
        canvas.addEventListener("touchstart", (e) => { client.input.fire = 1; this.touchAim(e); this.sendInput(); }, { passive: false });
        canvas.addEventListener("touchmove", (e) => { this.touchAim(e); e.preventDefault(); }, { passive: false });
        canvas.addEventListener("touchend", () => (client.input.fire = 0));
    }
}
